import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { PlusIcon } from '../../components/icons';
import { GroceriesIcon } from '../../components/NavIcons';
import { Skeleton } from '../../components/Skeleton';
import { NewGroceryListDialog } from '../../components/groceries/NewGroceryListDialog';
import { StoreNameLabel } from '../../components/groceries/StoreNameLabel';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { STATUS_LABEL, STATUS_ORDER, statusBadgeClass, type GroceryListStatus } from './display';
import { groceryKeys } from './queries';

interface GroceriesNavState {
  toast?: string;
}

export function GroceriesListPage() {
  const location = useLocation();
  const toast = (location.state as GroceriesNavState | null)?.toast;
  const [params, setParams] = useSearchParams();
  const [newOpen, setNewOpen] = useState(false);
  const navigate = useNavigate();

  const statusParam = params.get('status');
  const status: GroceryListStatus | '' =
    statusParam && (STATUS_ORDER as readonly string[]).includes(statusParam)
      ? (statusParam as GroceryListStatus)
      : '';

  const query = { status: status || undefined, limit: 50 };
  const lists = useQuery({
    queryKey: groceryKeys.list(query),
    queryFn: () => api.groceries.list(query),
  });

  function setStatus(next: GroceryListStatus | '') {
    setParams(next ? { status: next } : {}, { replace: true });
  }

  return (
    <AppShell title="Groceries" width="wide">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
            <button
              type="button"
              onClick={() => setStatus('')}
              aria-pressed={status === ''}
              className={`min-h-9 rounded-full border px-3.5 text-sm font-medium transition-colors ${
                status === ''
                  ? 'border-brand bg-brand text-white shadow-sm'
                  : 'border-line bg-surface text-ink hover:bg-brand-soft'
              }`}
            >
              All
            </button>
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(status === s ? '' : s)}
                aria-pressed={status === s}
                className={`min-h-9 rounded-full border px-3.5 text-sm font-medium transition-colors ${
                  status === s
                    ? 'border-brand bg-brand text-white shadow-sm'
                    : 'border-line bg-surface text-ink hover:bg-brand-soft'
                }`}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
          <Button block={false} className="sm:order-first" onClick={() => setNewOpen(true)}>
            <PlusIcon className="size-4" /> New list
          </Button>
        </div>

        {lists.isPending ? <Skeleton lines={4} label="Loading grocery lists" /> : null}
        {toast ? <Alert tone="info">{toast}</Alert> : null}
        {lists.isError ? <Alert tone="error">{messageForError(lists.error)}</Alert> : null}
        {lists.isSuccess && lists.data.lists.length === 0 ? (
          <div className="card">
            <EmptyState
              accent="groceries"
              icon={<GroceriesIcon className="size-full" />}
              title="No grocery lists yet"
              action={
                <Button block={false} onClick={() => setNewOpen(true)}>
                  <PlusIcon className="size-4" /> New list
                </Button>
              }
            >
              Start one from your planned meals.
            </EmptyState>
          </div>
        ) : null}

        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {lists.data?.lists.map((list) => (
            <li key={list.id}>
              <Link
                to={`/groceries/${list.id}`}
                className="card card-link flex h-full flex-col gap-2 p-4"
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-base font-semibold text-brand">
                    {list.name}
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(list.status)}`}
                  >
                    {STATUS_LABEL[list.status]}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-x-2 text-sm text-muted">
                  <span>
                    {list.checkedCount}/{list.itemCount} checked
                  </span>
                  {list.storeId ? (
                    <>
                      <span aria-hidden>·</span>
                      <StoreNameLabel storeId={list.storeId} />
                    </>
                  ) : null}
                </span>
                <span
                  aria-hidden
                  className="mt-auto block h-2 overflow-hidden rounded-full bg-brand-soft"
                >
                  <span
                    className="block h-full rounded-full bg-gradient-to-r from-brand to-accent"
                    style={{
                      width: `${list.itemCount ? Math.round((list.checkedCount / list.itemCount) * 100) : 0}%`,
                    }}
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <NewGroceryListDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(detail) => navigate(`/groceries/${detail.list.id}`)}
      />
    </AppShell>
  );
}
